import { useState, useEffect } from 'react';
import { formatDate, formatDateTime } from '../../utils/dateFormatting';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  CreditCard, Database, Settings, Loader2, CheckCircle, XCircle,
  Eye, EyeOff, Save, RefreshCw, AlertTriangle, Server, Package, DollarSign, Link2,
  HardDrive, Cloud, Mail, Send, FileText, Upload, Ticket, Coins, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const SystemSettings = () => {
  const { api, admin } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [mongoStatus, setMongoStatus] = useState(null);
  const [stripeTestResult, setStripeTestResult] = useState(null);
  
  // Form states
  const [stripeApiKey, setStripeApiKey] = useState('');
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [trialPeriodDays, setTrialPeriodDays] = useState(0);
  const [gracePeriodDays, setGracePeriodDays] = useState(14);
  const [mongoName, setMongoName] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [autoCloseDays, setAutoCloseDays] = useState(3);
  const [savingAutoClose, setSavingAutoClose] = useState(false);
  
  // PPD Tiers state
  const [ppdTiers, setPpdTiers] = useState([]);
  const [ppdLoading, setPpdLoading] = useState(false);
  const [ppdForm, setPpdForm] = useState({ name: '', credits: '', price_cents: '', stripe_price_id: '', currency: 'AUD' });
  const [ppdSaving, setPpdSaving] = useState(false);
  
  // Stripe Products state
  const [stripeProducts, setStripeProducts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsSaving, setProductsSaving] = useState(false);
  const [productForm, setProductForm] = useState({
    basic: { subscription: '', extra_operator: '', extra_operator_timesheet: '', extra_user: '' },
    advanced: { subscription: '', extra_operator: '', extra_operator_timesheet: '', extra_user: '' },
    professional: { subscription: '', extra_operator: '', extra_operator_timesheet: '', extra_user: '' }
  });
  const [validatingProduct, setValidatingProduct] = useState(null);
  
  // Storage Add-on Product state
  const [storageAddonProduct, setStorageAddonProduct] = useState(null);
  const [storageAddonForm, setStorageAddonForm] = useState('');
  const [storageAddonSaving, setStorageAddonSaving] = useState(false);
  
  // Integration credentials state
  const [integrations, setIntegrations] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationsSaving, setIntegrationsSaving] = useState(false);
  const [showMyobSecret, setShowMyobSecret] = useState(false);
  const [showXeroSecret, setShowXeroSecret] = useState(false);
  const [showQboSecret, setShowQboSecret] = useState(false);
  const [integrationForm, setIntegrationForm] = useState({
    myob_client_id: '',
    myob_client_secret: '',
    myob_redirect_uri: '',
    xero_client_id: '',
    xero_client_secret: '',
    xero_redirect_uri: '',
    qbo_client_id: '',
    qbo_client_secret: '',
    qbo_redirect_uri: '',
    qbo_environment: 'sandbox',
    frontend_url: '',
    google_places_api_key: ''
  });
  
  // Azure Storage state
  const [showConnString, setShowConnString] = useState(false);
  const [testingStorage, setTestingStorage] = useState(false);
  const [storageTestResult, setStorageTestResult] = useState(null);
  const [storageForm, setStorageForm] = useState({
    connection_string: '',
    container_name: 'compliance-documents',
    enabled: false,
    display_name: ''
  });
  
  // Resend Email state
  const [showResendApiKey, setShowResendApiKey] = useState(false);
  const [showResendWebhookSecret, setShowResendWebhookSecret] = useState(false);
  const [testingResend, setTestingResend] = useState(false);
  const [resendTestResult, setResendTestResult] = useState(null);
  const [resendForm, setResendForm] = useState({
    api_key: '',
    sender_email: '',
    sender_name: '',
    enabled: false,
    webhook_secret: ''
  });
  
  // Sales Configuration state
  const [salesEmail, setSalesEmail] = useState('');
  const [savingSalesEmail, setSavingSalesEmail] = useState(false);

  // Support inbox state
  const [supportInboxEmail, setSupportInboxEmail] = useState('');
  const [savingSupportInbox, setSavingSupportInbox] = useState(false);

  // JIRA integration state
  const [jiraSite, setJiraSite] = useState('');
  const [jiraEmail, setJiraEmail] = useState('');
  const [jiraToken, setJiraToken] = useState('');
  const [jiraTokenMasked, setJiraTokenMasked] = useState(null);
  const [savingJira, setSavingJira] = useState(false);
  const [jiraProjectKey, setJiraProjectKey] = useState('');
  const [jiraIssueTypeId, setJiraIssueTypeId] = useState('');
  const [jiraIssueTypeName, setJiraIssueTypeName] = useState('');
  const [jiraIssueTypes, setJiraIssueTypes] = useState([]);
  const [loadingIssueTypes, setLoadingIssueTypes] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchMongoStatus();
    fetchSalesSettings();
    fetchSupportInbox();
    fetchJiraSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/api/super-admin/settings');
      setSettings(res.data);
      setStripeEnabled(res.data.stripe_enabled || false);
      setTrialPeriodDays(res.data.trial_period_days || 0);
      setGracePeriodDays(res.data.payment_grace_period_days || 14);
      setMongoName(res.data.mongodb_connection_name || '');
      setAutoCloseDays(res.data.ticket_auto_close_days ?? 3);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSalesSettings = async () => {
    try {
      const res = await api.get('/api/super-admin/settings/sales');
      setSalesEmail(res.data.sales_email || '');
    } catch (err) {
      console.error('Failed to fetch sales settings:', err);
    }
  };
  
  const saveSalesSettings = async () => {
    setSavingSalesEmail(true);
    try {
      await api.put('/api/super-admin/settings/sales', { sales_email: salesEmail });
      toast.success('Sales email updated');
    } catch (err) {
      console.error('Failed to save sales settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save sales email');
    } finally {
      setSavingSalesEmail(false);
    }
  };

  const fetchSupportInbox = async () => {
    try {
      const res = await api.get('/api/super-admin/settings/support-inbox');
      setSupportInboxEmail(res.data.support_inbox_email || '');
    } catch (err) {
      console.error('Failed to fetch support inbox:', err);
    }
  };

  const saveSupportInbox = async () => {
    setSavingSupportInbox(true);
    try {
      await api.put('/api/super-admin/settings/support-inbox', { support_inbox_email: supportInboxEmail });
      toast.success('Support inbox updated');
    } catch (err) {
      console.error('Failed to save support inbox:', err);
      toast.error(err.response?.data?.detail || 'Failed to save support inbox');
    } finally {
      setSavingSupportInbox(false);
    }
  };

  const saveAutoCloseDays = async () => {
    setSavingAutoClose(true);
    try {
      await api.put('/api/super-admin/settings/ticket-auto-close', { days: autoCloseDays });
      toast.success(`Ticket auto-close set to ${autoCloseDays} days`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingAutoClose(false);
    }
  };

  const fetchJiraSettings = async () => {
    try {
      const res = await api.get('/api/super-admin/settings/jira');
      setJiraSite(res.data.jira_site || '');
      setJiraEmail(res.data.jira_email || '');
      setJiraTokenMasked(res.data.jira_api_token_masked || null);
      setJiraProjectKey(res.data.jira_project_key || '');
      setJiraIssueTypeId(res.data.jira_issue_type_id || '');
      setJiraIssueTypeName(res.data.jira_issue_type_name || '');
      // Auto-load issue types if credentials & project key are already configured
      if (res.data.configured && res.data.jira_project_key) {
        loadJiraIssueTypesForKey(res.data.jira_project_key);
      }
    } catch (err) {
      console.error('Failed to fetch JIRA settings:', err);
    }
  };

  const loadJiraIssueTypesForKey = async (projectKey) => {
    const pk = (projectKey || '').trim();
    if (!pk) return;
    setLoadingIssueTypes(true);
    try {
      const res = await api.get(`/api/super-admin/settings/jira/issue-types?project_key=${encodeURIComponent(pk)}`);
      if (res.data.ok) {
        setJiraIssueTypes(res.data.issue_types || []);
      } else {
        setJiraIssueTypes([]);
      }
    } catch {
      setJiraIssueTypes([]);
    } finally {
      setLoadingIssueTypes(false);
    }
  };

  const loadJiraIssueTypes = async () => {
    if (!jiraProjectKey.trim()) {
      toast.error('Enter a JIRA project key first');
      return;
    }
    setLoadingIssueTypes(true);
    try {
      const res = await api.get(`/api/super-admin/settings/jira/issue-types?project_key=${encodeURIComponent(jiraProjectKey.trim())}`);
      if (res.data.ok) {
        setJiraIssueTypes(res.data.issue_types || []);
        if (!res.data.issue_types?.length) toast.error('No issue types returned for this project');
      } else {
        setJiraIssueTypes([]);
        toast.error(`Could not load issue types (${res.data.error})`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load issue types');
    } finally {
      setLoadingIssueTypes(false);
    }
  };

  const saveJiraSettings = async () => {
    setSavingJira(true);
    try {
      const body = {
        jira_site: jiraSite,
        jira_email: jiraEmail,
        jira_project_key: jiraProjectKey,
        jira_issue_type_id: jiraIssueTypeId,
        jira_issue_type_name: jiraIssueTypeName,
      };
      // Only send the token when the admin typed a new one (blank keeps existing)
      if (jiraToken) body.jira_api_token = jiraToken;
      await api.put('/api/super-admin/settings/jira', body);
      toast.success('JIRA settings updated');
      setJiraToken('');
      fetchJiraSettings();
    } catch (err) {
      console.error('Failed to save JIRA settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save JIRA settings');
    } finally {
      setSavingJira(false);
    }
  };
  
  const fetchIntegrations = async () => {
    setIntegrationsLoading(true);
    try {
      const res = await api.get('/api/super-admin/settings/integrations');
      setIntegrations(res.data);
      setIntegrationForm({
        myob_client_id: res.data.myob_client_id || '',
        myob_client_secret: '',  // Never pre-fill secrets
        myob_redirect_uri: res.data.myob_redirect_uri || '',
        xero_client_id: res.data.xero_client_id || '',
        xero_client_secret: '',
        xero_redirect_uri: res.data.xero_redirect_uri || '',
        qbo_client_id: res.data.qbo_client_id || '',
        qbo_client_secret: '',
        qbo_redirect_uri: res.data.qbo_redirect_uri || '',
        qbo_environment: res.data.qbo_environment || 'sandbox',
        frontend_url: res.data.frontend_url || '',
        google_places_api_key: ''
      });
      
      // Populate storage form
      setStorageForm({
        connection_string: '',  // Never pre-fill connection strings
        container_name: res.data.azure_storage?.container_name || 'compliance-documents',
        enabled: res.data.azure_storage?.enabled || false,
        display_name: res.data.azure_storage?.display_name || ''
      });
      
      // Populate Resend form
      setResendForm({
        api_key: '',  // Never pre-fill API keys
        sender_email: res.data.resend?.sender_email || '',
        sender_name: res.data.resend?.sender_name || '',
        enabled: res.data.resend?.enabled || false,
        webhook_secret: '',  // Never pre-fill secrets
        webhook_configured: res.data.resend?.webhook_configured || false
      });
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
      toast.error('Failed to load integration settings');
    } finally {
      setIntegrationsLoading(false);
    }
  };
  
  const saveIntegrations = async () => {
    setIntegrationsSaving(true);
    try {
      // Only send non-empty values
      const updateData = {};
      Object.entries(integrationForm).forEach(([key, value]) => {
        if (value && value.trim()) {
          updateData[key] = value.trim();
        }
      });
      
      await api.put('/api/super-admin/settings/integrations', updateData);
      toast.success('Integration credentials saved');
      fetchIntegrations();  // Refresh to show updated status
    } catch (err) {
      console.error('Failed to save integrations:', err);
      toast.error('Failed to save integration credentials');
    } finally {
      setIntegrationsSaving(false);
    }
  };
  
  const testStorageConnection = async () => {
    setTestingStorage(true);
    try {
      const res = await api.get('/api/super-admin/settings/azure-storage/test');
      setStorageTestResult(res.data);
      
      if (res.data.connected) {
        toast.success('Storage connected successfully!');
      } else {
        toast.error(res.data.error || res.data.message || 'Connection failed');
      }
    } catch (err) {
      toast.error('Failed to test storage connection');
      setStorageTestResult({ connected: false, error: err.message });
    } finally {
      setTestingStorage(false);
    }
  };
  
  const saveStorageConfig = async () => {
    setIntegrationsSaving(true);
    try {
      const updateData = {
        azure_storage: {
          enabled: storageForm.enabled,
          container_name: storageForm.container_name || 'compliance-documents',
          display_name: storageForm.display_name || null
        }
      };
      
      // Only include connection string if it's being updated
      if (storageForm.connection_string && storageForm.connection_string.trim()) {
        updateData.azure_storage.connection_string = storageForm.connection_string.trim();
      }
      
      await api.put('/api/super-admin/settings/integrations', updateData);
      toast.success('Storage configuration saved');
      fetchIntegrations();  // Refresh
    } catch (err) {
      console.error('Failed to save storage config:', err);
      toast.error('Failed to save storage configuration');
    } finally {
      setIntegrationsSaving(false);
    }
  };

  const testResendConnection = async () => {
    setTestingResend(true);
    setResendTestResult(null);
    try {
      const res = await api.post('/api/super-admin/settings/resend/test');
      setResendTestResult(res.data);
      
      if (res.data.success) {
        toast.success('Test email sent successfully!');
      } else {
        toast.error(res.data.message || 'Connection failed');
      }
    } catch (err) {
      toast.error('Failed to test Resend connection');
      setResendTestResult({ success: false, message: err.message });
    } finally {
      setTestingResend(false);
    }
  };
  
  const saveResendConfig = async () => {
    setIntegrationsSaving(true);
    try {
      const updateData = {
        resend: {
          enabled: resendForm.enabled,
          sender_email: resendForm.sender_email || null,
          sender_name: resendForm.sender_name || null
        }
      };
      
      // Only include API key if it's being updated
      if (resendForm.api_key && resendForm.api_key.trim()) {
        updateData.resend.api_key = resendForm.api_key.trim();
      }
      
      // Only include webhook secret if it's being updated
      if (resendForm.webhook_secret && resendForm.webhook_secret.trim()) {
        updateData.resend.webhook_secret = resendForm.webhook_secret.trim();
      }
      
      await api.put('/api/super-admin/settings/integrations', updateData);
      toast.success('Resend configuration saved');
      fetchIntegrations();  // Refresh
      setResendForm(prev => ({ ...prev, api_key: '', webhook_secret: '' }));  // Clear sensitive fields
    } catch (err) {
      console.error('Failed to save Resend config:', err);
      toast.error('Failed to save Resend configuration');
    } finally {
      setIntegrationsSaving(false);
    }
  };

  // ─── PPD Tier management ─────────────────────────────────
  const fetchPpdTiers = async () => {
    setPpdLoading(true);
    try {
      const res = await api.get('/api/ppd/admin/tiers');
      setPpdTiers(res.data || []);
    } catch (e) { console.error('PPD tiers fetch error:', e); }
    finally { setPpdLoading(false); }
  };

  const handleCreatePpdTier = async () => {
    if (!ppdForm.name || !ppdForm.credits || !ppdForm.price_cents || !ppdForm.stripe_price_id) {
      toast.error('All fields are required'); return;
    }
    setPpdSaving(true);
    try {
      await api.post('/api/ppd/admin/tiers', {
        name: ppdForm.name,
        credits: parseInt(ppdForm.credits, 10),
        price_cents: parseInt(ppdForm.price_cents, 10),
        stripe_price_id: ppdForm.stripe_price_id,
        currency: ppdForm.currency || 'AUD',
        active: true,
      });
      toast.success('PPD tier created');
      setPpdForm({ name: '', credits: '', price_cents: '', stripe_price_id: '', currency: 'AUD' });
      fetchPpdTiers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create tier'); }
    finally { setPpdSaving(false); }
  };

  const handleDeletePpdTier = async (tierId) => {
    try {
      await api.delete(`/api/ppd/admin/tiers/${tierId}`);
      toast.success('PPD tier deactivated');
      fetchPpdTiers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to deactivate tier'); }
  };

  const fetchStripeProducts = async () => {
    setProductsLoading(true);
    try {
      const res = await api.get('/api/super-admin/settings/stripe/products');
      setStripeProducts(res.data);
      
      // Populate form with existing product IDs
      const newForm = {
        basic: { subscription: '', extra_operator: '', extra_operator_timesheet: '', extra_user: '' },
        advanced: { subscription: '', extra_operator: '', extra_operator_timesheet: '', extra_user: '' },
        professional: { subscription: '', extra_operator: '', extra_operator_timesheet: '', extra_user: '' }
      };
      
      ['basic', 'advanced', 'professional'].forEach(tier => {
        if (res.data[tier]) {
          if (res.data[tier].subscription?.product_id) {
            newForm[tier].subscription = res.data[tier].subscription.product_id;
          }
          if (res.data[tier].extra_operator?.product_id) {
            newForm[tier].extra_operator = res.data[tier].extra_operator.product_id;
          }
          if (res.data[tier].extra_operator_timesheet?.product_id) {
            newForm[tier].extra_operator_timesheet = res.data[tier].extra_operator_timesheet.product_id;
          }
          if (res.data[tier].extra_user?.product_id) {
            newForm[tier].extra_user = res.data[tier].extra_user.product_id;
          }
        }
      });
      
      setProductForm(newForm);
    } catch (err) {
      console.error('Failed to fetch Stripe products:', err);
      toast.error('Failed to load Stripe products');
    } finally {
      setProductsLoading(false);
    }
  };

  const validateProduct = async (productId, tier, productType) => {
    if (!productId || !productId.startsWith('prod_')) {
      toast.error('Invalid product ID. Must start with prod_');
      return;
    }
    
    setValidatingProduct(`${tier}_${productType}`);
    try {
      const res = await api.post('/api/super-admin/settings/stripe/products/validate', { product_id: productId });
      if (res.data.valid) {
        toast.success(`Valid: ${res.data.product_name} - $${res.data.price?.amount} ${res.data.price?.currency?.toUpperCase()}/${res.data.price?.interval}`);
      } else {
        toast.error(res.data.error);
      }
    } catch (err) {
      toast.error('Failed to validate product');
    } finally {
      setValidatingProduct(null);
    }
  };

  const saveStripeProducts = async () => {
    setProductsSaving(true);
    try {
      const products = {
        currency: 'aud',
        basic: {
          subscription: productForm.basic.subscription ? { product_id: productForm.basic.subscription } : null,
          extra_operator: productForm.basic.extra_operator ? { product_id: productForm.basic.extra_operator } : null,
          extra_user: productForm.basic.extra_user ? { product_id: productForm.basic.extra_user } : null
        },
        advanced: {
          subscription: productForm.advanced.subscription ? { product_id: productForm.advanced.subscription } : null,
          extra_operator: productForm.advanced.extra_operator ? { product_id: productForm.advanced.extra_operator } : null,
          extra_operator_timesheet: productForm.advanced.extra_operator_timesheet ? { product_id: productForm.advanced.extra_operator_timesheet } : null,
          extra_user: productForm.advanced.extra_user ? { product_id: productForm.advanced.extra_user } : null
        },
        professional: {
          subscription: productForm.professional.subscription ? { product_id: productForm.professional.subscription } : null,
          extra_operator: productForm.professional.extra_operator ? { product_id: productForm.professional.extra_operator } : null,
          extra_operator_timesheet: productForm.professional.extra_operator_timesheet ? { product_id: productForm.professional.extra_operator_timesheet } : null,
          extra_user: productForm.professional.extra_user ? { product_id: productForm.professional.extra_user } : null
        }
      };
      
      await api.put('/api/super-admin/settings/stripe/products', { products });
      toast.success('Stripe products saved successfully');
      fetchStripeProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save products');
    } finally {
      setProductsSaving(false);
    }
  };

  // Storage Add-on Product functions
  const fetchStorageAddonProduct = async () => {
    try {
      const res = await api.get('/api/storage/admin/addon-product');
      setStorageAddonProduct(res.data.product);
      if (res.data.product) {
        setStorageAddonForm(res.data.product.product_id || '');
      }
    } catch (err) {
      console.error('Failed to fetch storage addon product:', err);
    }
  };

  const saveStorageAddonProduct = async () => {
    if (!storageAddonForm) {
      // Remove the storage addon product
      setStorageAddonSaving(true);
      try {
        await api.delete('/api/storage/admin/addon-product');
        setStorageAddonProduct(null);
        toast.success('Storage add-on product removed');
      } catch (err) {
        toast.error('Failed to remove storage add-on product');
      } finally {
        setStorageAddonSaving(false);
      }
      return;
    }

    // Validate product ID format
    if (!storageAddonForm.startsWith('prod_')) {
      toast.error('Invalid product ID. Must start with prod_');
      return;
    }

    setStorageAddonSaving(true);
    try {
      // Validate the product exists in Stripe
      const validateRes = await api.post('/api/super-admin/settings/stripe/products/validate', { product_id: storageAddonForm });
      if (!validateRes.data.valid) {
        toast.error(validateRes.data.error || 'Invalid Stripe product');
        setStorageAddonSaving(false);
        return;
      }

      // Check if we have a price
      if (!validateRes.data.price?.id) {
        toast.error('This product does not have a recurring price configured in Stripe');
        setStorageAddonSaving(false);
        return;
      }

      // Save the storage addon product
      await api.post('/api/storage/admin/addon-product', {
        product_id: storageAddonForm,
        price_id: validateRes.data.price.id,
        name: validateRes.data.product_name
      });
      
      toast.success(`Storage add-on product saved: ${validateRes.data.product_name}`);
      fetchStorageAddonProduct();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save storage add-on product');
    } finally {
      setStorageAddonSaving(false);
    }
  };

  const fetchMongoStatus = async () => {
    try {
      const res = await api.get('/api/super-admin/settings/mongodb/status');
      setMongoStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch MongoDB status:', err);
    }
  };

  const testStripeConnection = async () => {
    setTesting(true);
    setStripeTestResult(null);
    try {
      const res = await api.get('/api/super-admin/settings/stripe/test');
      setStripeTestResult(res.data);
      if (res.data.success) {
        toast.success('Stripe connection successful!');
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const saveStripeSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        stripe_enabled: stripeEnabled,
        trial_period_days: trialPeriodDays,
        payment_grace_period_days: gracePeriodDays
      };
      
      // Only include keys if they were changed (not empty)
      if (stripeApiKey) {
        payload.stripe_api_key = stripeApiKey;
      }
      if (stripeWebhookSecret) {
        payload.stripe_webhook_secret = stripeWebhookSecret;
      }
      
      await api.put('/api/super-admin/settings/stripe', payload);
      toast.success('Stripe settings saved');
      setStripeApiKey('');
      setStripeWebhookSecret('');
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveMongoName = async () => {
    setSaving(true);
    try {
      await api.put('/api/super-admin/settings/mongodb-name', { name: mongoName });
      toast.success('MongoDB name saved');
      fetchSettings();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  const isAdmin = admin?.role === 'admin';

  return (
    <div className="space-y-6">
      <div>
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
          <Settings className="h-6 w-6 text-[#ffd108]" />
          System Settings
        </h1>
        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Configure integrations and system preferences</p>
      </div>

      <Tabs defaultValue="stripe" className="space-y-4">
        <TabsList className={isDark ? 'bg-slate-800' : 'bg-gray-100'}>
          <TabsTrigger value="stripe" className="data-[state=active]:bg-[#ffd108] data-[state=active]:text-black">
            <CreditCard className="h-4 w-4 mr-2" /> Stripe
          </TabsTrigger>
          <TabsTrigger 
            value="products" 
            className="data-[state=active]:bg-[#ffd108] data-[state=active]:text-black"
            onClick={() => {
              if (!stripeProducts) fetchStripeProducts();
              if (!storageAddonProduct) fetchStorageAddonProduct();
              if (!ppdTiers.length) fetchPpdTiers();
            }}
          >
            <Package className="h-4 w-4 mr-2" /> Stripe Products
          </TabsTrigger>
          <TabsTrigger 
            value="storage" 
            className="data-[state=active]:bg-[#ffd108] data-[state=active]:text-black"
            onClick={() => !integrations && fetchIntegrations()}
          >
            <Cloud className="h-4 w-4 mr-2" /> Storage
          </TabsTrigger>
          <TabsTrigger 
            value="integrations" 
            className="data-[state=active]:bg-[#ffd108] data-[state=active]:text-black"
            onClick={() => !integrations && fetchIntegrations()}
          >
            <Link2 className="h-4 w-4 mr-2" /> Integrations
          </TabsTrigger>
          <TabsTrigger value="database" className="data-[state=active]:bg-[#ffd108] data-[state=active]:text-black">
            <Database className="h-4 w-4 mr-2" /> Database
          </TabsTrigger>
          <TabsTrigger value="legal" className="data-[state=active]:bg-[#ffd108] data-[state=active]:text-black">
            <FileText className="h-4 w-4 mr-2" /> Legal
          </TabsTrigger>
          <TabsTrigger value="tickets" className="data-[state=active]:bg-[#ffd108] data-[state=active]:text-black">
            <Ticket className="h-4 w-4 mr-2" /> Tickets
          </TabsTrigger>

        </TabsList>

        {/* Stripe Settings */}
        <TabsContent value="stripe" className="space-y-4">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <CreditCard className="h-5 w-5 text-[#ffd108]" />
                Stripe Configuration
              </CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                Configure Stripe for payment processing. Get your API keys from the{' '}
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#ffd108] hover:underline">
                  Stripe Dashboard
                </a>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              <div className={`flex items-center justify-between p-4 rounded-lg ${isDark ? 'bg-slate-900' : 'bg-gray-100'}`}>
                <div className="flex items-center gap-3">
                  {settings?.stripe_enabled ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className={`h-5 w-5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                  )}
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Stripe Integration</p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {settings?.stripe_api_key_last4 
                        ? `API Key: ****${settings.stripe_api_key_last4}`
                        : 'No API key configured'}
                    </p>
                  </div>
                </div>
                <Badge className={settings?.stripe_enabled ? 'bg-green-500/20 text-green-400' : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-gray-500'}>
                  {settings?.stripe_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>

              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className={isDark ? 'text-white' : 'text-gray-900'}>Enable Stripe Payments</Label>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Allow subscription and resource purchases</p>
                </div>
                <Switch
                  checked={stripeEnabled}
                  onCheckedChange={setStripeEnabled}
                  disabled={!isAdmin}
                />
              </div>

              {/* API Key */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Secret API Key</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showApiKey ? 'text' : 'password'}
                      value={stripeApiKey}
                      onChange={(e) => setStripeApiKey(e.target.value)}
                      placeholder={settings?.stripe_api_key_last4 ? `Current: ****${settings.stripe_api_key_last4}` : 'sk_live_... or sk_test_...'}
                      className={`${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} pr-10`}
                      disabled={!isAdmin}
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Leave blank to keep existing key</p>
              </div>

              {/* Webhook Secret */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Webhook Secret</Label>
                <div className="relative">
                  <Input
                    type={showWebhookSecret ? 'text' : 'password'}
                    value={stripeWebhookSecret}
                    onChange={(e) => setStripeWebhookSecret(e.target.value)}
                    placeholder={settings?.stripe_webhook_configured ? 'Webhook configured ✓' : 'whsec_...'}
                    className={`${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} pr-10`}
                    disabled={!isAdmin}
                  />
                  <button
                    type="button"
                    onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    {showWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  Create a webhook in Stripe pointing to: <code className={`${isDark ? 'bg-slate-900' : 'bg-gray-200'} px-1 rounded`}>https://your-domain.com/api/webhook/stripe</code>
                </p>
              </div>

              {/* Subscription Settings */}
              <div className={`space-y-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Subscription Settings</h4>
                
                {/* Trial Period */}
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Trial Period (days)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="365"
                    value={trialPeriodDays}
                    onChange={(e) => setTrialPeriodDays(parseInt(e.target.value) || 0)}
                    className={`${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} w-32`}
                    disabled={!isAdmin}
                  />
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    Number of days new customers can use the service before being charged. Set to 0 for no trial.
                  </p>
                </div>

                {/* Grace Period */}
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Payment Grace Period (days)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="90"
                    value={gracePeriodDays}
                    onChange={(e) => setGracePeriodDays(parseInt(e.target.value) || 14)}
                    className={`${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} w-32`}
                    disabled={!isAdmin}
                  />
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    Days after a failed payment before account access is suspended. Default is 14 days.
                  </p>
                </div>
              </div>

              {/* Test Result */}
              {stripeTestResult && (
                <Alert className={stripeTestResult.success ? 'bg-green-500/10 border-green-500' : 'bg-red-500/10 border-red-500'}>
                  <AlertDescription className={stripeTestResult.success ? 'text-green-400' : 'text-red-400'}>
                    {stripeTestResult.success ? (
                      <div>
                        <p className="font-medium">✓ Connection Successful</p>
                        <p className="text-sm">Account: {stripeTestResult.account_name || stripeTestResult.account_id}</p>
                        <p className="text-sm">Country: {stripeTestResult.country} • Currency: {stripeTestResult.default_currency?.toUpperCase()}</p>
                      </div>
                    ) : (
                      <p>✗ {stripeTestResult.message}</p>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Actions */}
              <div className={`flex gap-3 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <Button
                  variant="outline"
                  onClick={testStripeConnection}
                  disabled={testing}
                  className={isDark ? 'border-slate-600' : 'border-gray-300'}
                >
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Test Connection
                </Button>
                {isAdmin && (
                  <Button
                    onClick={saveStripeSettings}
                    disabled={saving}
                    className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stripe Products Settings */}
        <TabsContent value="products" className="space-y-4">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Package className="h-5 w-5 text-[#ffd108]" />
                Stripe Products Configuration
              </CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                Configure Stripe Product IDs for each subscription tier. These products are used for recurring billing when users purchase extra operators or users.
                Create products in your{' '}
                <a href="https://dashboard.stripe.com/products" target="_blank" rel="noopener noreferrer" className="text-[#ffd108] hover:underline">
                  Stripe Dashboard
                </a>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {productsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
                </div>
              ) : (
                <>
                  {/* Info Alert */}
                  <Alert className={isDark ? 'bg-blue-500/10 border-blue-500' : 'bg-blue-50 border-blue-200'}>
                    <DollarSign className="h-4 w-4 text-blue-400" />
                    <AlertDescription className={isDark ? 'text-blue-300' : 'text-blue-700'}>
                      <p className="font-medium mb-1">Product ID Format</p>
                      <p className="text-sm">
                        Enter the Product ID from Stripe (starts with <code className={`${isDark ? 'bg-slate-800' : 'bg-blue-100'} px-1 rounded`}>prod_</code>). 
                        The system will automatically fetch the associated recurring price. 
                        Make sure each product has a monthly recurring price configured in Stripe.
                      </p>
                    </AlertDescription>
                  </Alert>

                  {/* Tier Products */}
                  {['basic', 'advanced', 'professional'].map((tier) => (
                    <div key={tier} className={`p-4 rounded-lg space-y-4 ${isDark ? 'bg-slate-900' : 'bg-gray-100'}`}>
                      <div className="flex items-center gap-2">
                        <h3 className={`text-lg font-semibold capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>{tier} Tier</h3>
                        {stripeProducts?.[tier] && (stripeProducts[tier].subscription?.product_id || stripeProducts[tier].extra_operator?.product_id || stripeProducts[tier].extra_user?.product_id) && (
                          <Badge className="bg-green-500/20 text-green-400">Configured</Badge>
                        )}
                      </div>
                      
                      {/* Subscription Product */}
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Subscription Plan Product ID</Label>
                        <div className="flex gap-2">
                          <Input
                            value={productForm[tier].subscription || ''}
                            onChange={(e) => setProductForm(prev => ({
                              ...prev,
                              [tier]: { ...prev[tier], subscription: e.target.value }
                            }))}
                            placeholder="prod_xxxxxxxxxxxxx"
                            className={`${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} font-mono text-sm`}
                            disabled={!isAdmin}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => validateProduct(productForm[tier].subscription, tier, 'subscription')}
                            disabled={!productForm[tier].subscription || validatingProduct === `${tier}_subscription`}
                            className={isDark ? 'border-slate-600' : 'border-gray-300'}
                          >
                            {validatingProduct === `${tier}_subscription` ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        {stripeProducts?.[tier]?.subscription?.product_name && (
                          <p className="text-xs text-green-400">
                            ✓ {stripeProducts[tier].subscription.product_name} - ${stripeProducts[tier].subscription.amount} {stripeProducts[tier].subscription.currency?.toUpperCase()}/{stripeProducts[tier].subscription.interval}
                          </p>
                        )}
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Extra Operator */}
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Extra Operator Product ID</Label>
                          <div className="flex gap-2">
                            <Input
                              value={productForm[tier].extra_operator}
                              onChange={(e) => setProductForm(prev => ({
                                ...prev,
                                [tier]: { ...prev[tier], extra_operator: e.target.value }
                              }))}
                              placeholder="prod_xxxxxxxxxxxxx"
                              className={`${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} font-mono text-sm`}
                              disabled={!isAdmin}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => validateProduct(productForm[tier].extra_operator, tier, 'extra_operator')}
                              disabled={!productForm[tier].extra_operator || validatingProduct === `${tier}_extra_operator`}
                              className={isDark ? 'border-slate-600' : 'border-gray-300'}
                            >
                              {validatingProduct === `${tier}_extra_operator` ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <CheckCircle className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          {stripeProducts?.[tier]?.extra_operator?.product_name && (
                            <p className="text-xs text-green-400">
                              ✓ {stripeProducts[tier].extra_operator.product_name} - ${stripeProducts[tier].extra_operator.amount} {stripeProducts[tier].extra_operator.currency?.toUpperCase()}/{stripeProducts[tier].extra_operator.interval}
                            </p>
                          )}
                        </div>

                        {/* Extra User */}
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Extra User Product ID</Label>
                          <div className="flex gap-2">
                            <Input
                              value={productForm[tier].extra_user}
                              onChange={(e) => setProductForm(prev => ({
                                ...prev,
                                [tier]: { ...prev[tier], extra_user: e.target.value }
                              }))}
                              placeholder="prod_xxxxxxxxxxxxx"
                              className={`${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} font-mono text-sm`}
                              disabled={!isAdmin}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => validateProduct(productForm[tier].extra_user, tier, 'extra_user')}
                              disabled={!productForm[tier].extra_user || validatingProduct === `${tier}_extra_user`}
                              className={isDark ? 'border-slate-600' : 'border-gray-300'}
                            >
                              {validatingProduct === `${tier}_extra_user` ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <CheckCircle className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          {stripeProducts?.[tier]?.extra_user?.product_name && (
                            <p className="text-xs text-green-400">
                              ✓ {stripeProducts[tier].extra_user.product_name} - ${stripeProducts[tier].extra_user.amount} {stripeProducts[tier].extra_user.currency?.toUpperCase()}/{stripeProducts[tier].extra_user.interval}
                            </p>
                          )}
                        </div>

                        {/* Extra Timesheet-Only Licence (Advanced/Professional only) */}
                        {tier !== 'basic' && (
                          <div className="space-y-2">
                            <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Timesheet Licence Product ID</Label>
                            <div className="flex gap-2">
                              <Input
                                value={productForm[tier].extra_operator_timesheet}
                                onChange={(e) => setProductForm(prev => ({
                                  ...prev,
                                  [tier]: { ...prev[tier], extra_operator_timesheet: e.target.value }
                                }))}
                                placeholder="prod_xxxxxxxxxxxxx"
                                className={`${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} font-mono text-sm`}
                                disabled={!isAdmin}
                                data-testid={`stripe-product-timesheet-${tier}-input`}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => validateProduct(productForm[tier].extra_operator_timesheet, tier, 'extra_operator_timesheet')}
                                disabled={!productForm[tier].extra_operator_timesheet || validatingProduct === `${tier}_extra_operator_timesheet`}
                                className={isDark ? 'border-slate-600' : 'border-gray-300'}
                              >
                                {validatingProduct === `${tier}_extra_operator_timesheet` ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <CheckCircle className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                            {stripeProducts?.[tier]?.extra_operator_timesheet?.product_name && (
                              <p className="text-xs text-green-400">
                                ✓ {stripeProducts[tier].extra_operator_timesheet.product_name} - ${stripeProducts[tier].extra_operator_timesheet.amount} {stripeProducts[tier].extra_operator_timesheet.currency?.toUpperCase()}/{stripeProducts[tier].extra_operator_timesheet.interval}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">Timesheet-only mobile licence (restricted app access)</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Storage Add-on Product */}
                  <div className={`p-4 rounded-lg space-y-4 ${isDark ? 'bg-slate-900' : 'bg-gray-100'}`}>
                    <div className="flex items-center gap-2">
                      <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Storage Add-on (+50GB)
                      </h3>
                      {storageAddonProduct && (
                        <Badge className="bg-green-500/20 text-green-400">Configured</Badge>
                      )}
                    </div>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Configure a Stripe product for storage add-ons. Tenants can purchase additional 50GB storage blocks when they reach their plan limit.
                    </p>
                    <div className="space-y-2">
                      <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Storage Add-on Product ID</Label>
                      <div className="flex gap-2">
                        <Input
                          value={storageAddonForm}
                          onChange={(e) => setStorageAddonForm(e.target.value)}
                          placeholder="prod_xxxxxxxxxxxxx (leave empty to disable)"
                          className={`${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} font-mono text-sm`}
                          disabled={!isAdmin}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={saveStorageAddonProduct}
                          disabled={storageAddonSaving}
                          className={isDark ? 'border-slate-600' : 'border-gray-300'}
                        >
                          {storageAddonSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      {storageAddonProduct && (
                        <p className="text-xs text-green-400">
                          ✓ {storageAddonProduct.name}
                        </p>
                      )}
                      {!storageAddonProduct && (
                        <p className="text-xs text-amber-400">
                          No storage add-on configured. Tenants cannot purchase additional storage.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Save Button */}
                  {isAdmin && (
                    <div className={`flex gap-3 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                      <Button
                        variant="outline"
                        onClick={fetchStripeProducts}
                        disabled={productsLoading}
                        className={isDark ? 'border-slate-600' : 'border-gray-300'}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                      </Button>
                      <Button
                        onClick={saveStripeProducts}
                        disabled={productsSaving}
                        className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                      >
                        {productsSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Products
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* PPD Credit Pack Tiers */}
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Coins className="h-5 w-5 text-[#ffd108]" /> PPD Credit Pack Tiers</CardTitle>
              <CardDescription>Define credit packs that tenants can purchase. Each pack maps to a Stripe one-time price.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button size="sm" variant="outline" onClick={fetchPpdTiers} disabled={ppdLoading} data-testid="ppd-refresh-tiers">
                {ppdLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />} Refresh
              </Button>

              {/* Existing tiers */}
              {ppdTiers.length > 0 && (
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium">Name</th>
                        <th className="px-3 py-2 text-right text-xs font-medium">Credits</th>
                        <th className="px-3 py-2 text-right text-xs font-medium">Price</th>
                        <th className="px-3 py-2 text-left text-xs font-medium">Stripe Price ID</th>
                        <th className="px-3 py-2 text-center text-xs font-medium">Status</th>
                        <th className="px-3 py-2 text-xs font-medium"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {ppdTiers.map((t) => (
                        <tr key={t.id}>
                          <td className="px-3 py-2">{t.name}</td>
                          <td className="px-3 py-2 text-right font-medium">{t.credits}</td>
                          <td className="px-3 py-2 text-right">${(t.price_cents / 100).toFixed(2)} {t.currency}</td>
                          <td className="px-3 py-2 text-xs font-mono text-muted-foreground truncate max-w-[200px]">{t.stripe_price_id}</td>
                          <td className="px-3 py-2 text-center">
                            <Badge variant="secondary" className={t.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                              {t.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-3 py-2">
                            {t.active && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => handleDeletePpdTier(t.id)} data-testid={`ppd-delete-tier-${t.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Add new tier */}
              <div className="border rounded-md p-4 space-y-3 bg-muted/20">
                <h4 className="text-sm font-medium flex items-center gap-2"><Plus className="h-4 w-4" /> Add New Credit Pack</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Pack Name</Label>
                    <Input value={ppdForm.name} onChange={(e) => setPpdForm(f => ({...f, name: e.target.value}))} placeholder="e.g. Starter Pack" className="h-8" data-testid="ppd-tier-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Credits</Label>
                    <Input type="number" value={ppdForm.credits} onChange={(e) => setPpdForm(f => ({...f, credits: e.target.value}))} placeholder="50" className="h-8" data-testid="ppd-tier-credits" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Price (cents)</Label>
                    <Input type="number" value={ppdForm.price_cents} onChange={(e) => setPpdForm(f => ({...f, price_cents: e.target.value}))} placeholder="19900" className="h-8" data-testid="ppd-tier-price" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Currency</Label>
                    <Input value={ppdForm.currency} onChange={(e) => setPpdForm(f => ({...f, currency: e.target.value.toUpperCase()}))} placeholder="AUD" className="h-8" maxLength={3} data-testid="ppd-tier-currency" />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Stripe Price ID</Label>
                    <Input value={ppdForm.stripe_price_id} onChange={(e) => setPpdForm(f => ({...f, stripe_price_id: e.target.value}))} placeholder="price_..." className="h-8 font-mono text-xs" data-testid="ppd-tier-stripe-id" />
                  </div>
                </div>
                <Button size="sm" className="btn-volt" onClick={handleCreatePpdTier} disabled={ppdSaving} data-testid="ppd-create-tier">
                  {ppdSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Create Tier
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Storage Settings */}
        <TabsContent value="storage" className="space-y-4">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Cloud className="h-5 w-5 text-[#ffd108]" />
                Azure Blob Storage
              </CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                Configure Azure Blob Storage for document uploads (compliance records, forms, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {integrationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
                </div>
              ) : (
                <>
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Storage Configuration
                      </h3>
                      <div className="flex items-center gap-2">
                        <Label className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Enable</Label>
                        <Switch
                          checked={storageForm.enabled}
                          onCheckedChange={(checked) => setStorageForm(prev => ({ ...prev, enabled: checked }))}
                          disabled={!isAdmin}
                        />
                      </div>
                    </div>
                    
                    {/* Status indicator */}
                    {integrations?.azure_storage?.configured && (
                      <div className={`flex items-center gap-2 mb-4 p-2 rounded ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                          Connected to: <span className="font-medium">{integrations.azure_storage.account_name}</span>
                          {integrations.azure_storage.display_name && (
                            <span className="text-slate-400"> ({integrations.azure_storage.display_name})</span>
                          )}
                        </span>
                        {integrations.azure_storage.enabled && (
                          <Badge className="bg-green-500/20 text-green-400 ml-2">Active</Badge>
                        )}
                      </div>
                    )}
                    
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Display Name (Optional)</Label>
                        <Input
                          value={storageForm.display_name}
                          onChange={(e) => setStorageForm(prev => ({ ...prev, display_name: e.target.value }))}
                          placeholder="e.g., EQUIPR CORE Storage"
                          className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                          data-testid="storage-name-input"
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Connection String</Label>
                        <div className="relative">
                          <Input
                            type={showConnString ? 'text' : 'password'}
                            value={storageForm.connection_string}
                            onChange={(e) => setStorageForm(prev => ({ ...prev, connection_string: e.target.value }))}
                            placeholder={integrations?.azure_storage?.configured ? '••••••••••••••• (configured)' : 'DefaultEndpointsProtocol=https;AccountName=...'}
                            className={`pr-10 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                            data-testid="connection-string-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowConnString(!showConnString)}
                          >
                            {showConnString ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          Find this in Azure Portal → Storage Account → Access keys
                        </p>
                      </div>
                      
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Container Name</Label>
                        <Input
                          value={storageForm.container_name}
                          onChange={(e) => setStorageForm(prev => ({ ...prev, container_name: e.target.value }))}
                          placeholder="compliance-documents"
                          className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                          data-testid="container-name-input"
                        />
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          Container will be created automatically if it doesn't exist
                        </p>
                      </div>
                      
                      {/* Test Results */}
                      {storageTestResult && (
                        <div className={`p-3 rounded ${storageTestResult.connected ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                          {storageTestResult.connected ? (
                            <div className="space-y-1">
                              <p className="text-green-400 font-medium flex items-center gap-2">
                                <CheckCircle className="h-4 w-4" /> Connection Successful
                              </p>
                              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                                Account: {storageTestResult.account_name} | Containers: {storageTestResult.container_count}
                                {storageTestResult.container_verified && ` | Verified: ${storageTestResult.container_verified}`}
                              </p>
                            </div>
                          ) : (
                            <p className="text-red-400 flex items-center gap-2">
                              <XCircle className="h-4 w-4" /> {storageTestResult.error || storageTestResult.message}
                            </p>
                          )}
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={testStorageConnection}
                          disabled={testingStorage || !integrations?.azure_storage?.configured}
                          className={isDark ? 'border-slate-600' : ''}
                        >
                          {testingStorage ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-2" />
                          )}
                          Test Connection
                        </Button>
                        <Button
                          onClick={saveStorageConfig}
                          disabled={integrationsSaving || !isAdmin}
                          className="bg-[#ffd108] text-black hover:bg-[#e5bc00]"
                        >
                          {integrationsSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                          Save Configuration
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <Alert className={isDark ? 'bg-slate-900 border-slate-700' : 'bg-blue-50 border-blue-200'}>
                    <HardDrive className={`h-4 w-4 ${isDark ? 'text-[#ffd108]' : 'text-blue-600'}`} />
                    <AlertDescription className={isDark ? 'text-slate-300' : 'text-gray-600'}>
                      <strong>How it works:</strong> When storage is configured and enabled, document uploads (compliance records, forms) will be stored in Azure Blob Storage. 
                      If not enabled, uploads fall back to local server storage.
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Settings */}
        <TabsContent value="integrations" className="space-y-4">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Link2 className="h-5 w-5 text-[#ffd108]" />
                Integration Credentials
              </CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                Configure API credentials for third-party integrations. These credentials will be used by all tenants.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {integrationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
                </div>
              ) : (
                <>
                  {/* MYOB Section */}
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                    <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      MYOB Configuration
                    </h3>
                    
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Client ID</Label>
                        <Input
                          value={integrationForm.myob_client_id}
                          onChange={(e) => setIntegrationForm(prev => ({ ...prev, myob_client_id: e.target.value }))}
                          placeholder="Enter MYOB Client ID"
                          className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                          data-testid="myob-client-id-input"
                        />
                        {integrations?.myob_client_id && (
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            Current: {integrations.myob_client_id}
                          </p>
                        )}
                      </div>
                      
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Client Secret</Label>
                        <div className="relative">
                          <Input
                            type={showMyobSecret ? 'text' : 'password'}
                            value={integrationForm.myob_client_secret}
                            onChange={(e) => setIntegrationForm(prev => ({ ...prev, myob_client_secret: e.target.value }))}
                            placeholder={integrations?.myob_client_secret_configured ? '••••••••••••••• (configured)' : 'Enter MYOB Client Secret'}
                            className={`pr-10 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                            data-testid="myob-client-secret-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowMyobSecret(!showMyobSecret)}
                          >
                            {showMyobSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                        {integrations?.myob_client_secret_configured && (
                          <Badge variant="outline" className="w-fit text-green-500 border-green-500">
                            <CheckCircle className="h-3 w-3 mr-1" /> Secret Configured
                          </Badge>
                        )}
                      </div>
                      
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Redirect URI</Label>
                        <Input
                          value={integrationForm.myob_redirect_uri}
                          onChange={(e) => setIntegrationForm(prev => ({ ...prev, myob_redirect_uri: e.target.value }))}
                          placeholder="https://yourdomain.com/api/myob/callback"
                          className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                          data-testid="myob-redirect-uri-input"
                        />
                        {integrations?.myob_redirect_uri && (
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            Current: {integrations.myob_redirect_uri}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Xero Section */}
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                    <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Xero Configuration
                    </h3>
                    
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Client ID</Label>
                        <Input
                          value={integrationForm.xero_client_id}
                          onChange={(e) => setIntegrationForm(prev => ({ ...prev, xero_client_id: e.target.value }))}
                          placeholder="Enter Xero Client ID"
                          className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                          data-testid="xero-client-id-input"
                        />
                        {integrations?.xero_client_id && (
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            Current: {integrations.xero_client_id}
                          </p>
                        )}
                      </div>
                      
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Client Secret</Label>
                        <div className="relative">
                          <Input
                            type={showXeroSecret ? 'text' : 'password'}
                            value={integrationForm.xero_client_secret}
                            onChange={(e) => setIntegrationForm(prev => ({ ...prev, xero_client_secret: e.target.value }))}
                            placeholder={integrations?.xero_client_secret_configured ? '••••••••••••••• (configured)' : 'Enter Xero Client Secret'}
                            className={`pr-10 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                            data-testid="xero-client-secret-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowXeroSecret(!showXeroSecret)}
                          >
                            {showXeroSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                        {integrations?.xero_client_secret_configured && (
                          <Badge variant="outline" className="w-fit text-green-500 border-green-500">
                            <CheckCircle className="h-3 w-3 mr-1" /> Secret Configured
                          </Badge>
                        )}
                      </div>
                      
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Redirect URI</Label>
                        <Input
                          value={integrationForm.xero_redirect_uri}
                          onChange={(e) => setIntegrationForm(prev => ({ ...prev, xero_redirect_uri: e.target.value }))}
                          placeholder="https://yourdomain.com/api/xero/callback"
                          className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                          data-testid="xero-redirect-uri-input"
                        />
                        {integrations?.xero_redirect_uri && (
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            Current: {integrations.xero_redirect_uri}
                          </p>
                        )}
                      </div>
                      
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Frontend URL (for OAuth redirect)</Label>
                        <Input
                          value={integrationForm.frontend_url}
                          onChange={(e) => setIntegrationForm(prev => ({ ...prev, frontend_url: e.target.value }))}
                          placeholder="https://app.yourdomain.com"
                          className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                          data-testid="frontend-url-input"
                        />
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          Required when backend and frontend are on different domains. Users will be redirected here after OAuth.
                        </p>
                        {integrations?.frontend_url && (
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            Current: {integrations.frontend_url}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* QuickBooks Online Section */}
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                    <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      QuickBooks Online Configuration
                    </h3>
                    
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Client ID</Label>
                        <Input
                          value={integrationForm.qbo_client_id}
                          onChange={(e) => setIntegrationForm(prev => ({ ...prev, qbo_client_id: e.target.value }))}
                          placeholder="Enter QuickBooks Client ID"
                          className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                          data-testid="qbo-client-id-input"
                        />
                        {integrations?.qbo_client_id && (
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            Current: {integrations.qbo_client_id}
                          </p>
                        )}
                      </div>
                      
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Client Secret</Label>
                        <div className="relative">
                          <Input
                            type={showQboSecret ? 'text' : 'password'}
                            value={integrationForm.qbo_client_secret}
                            onChange={(e) => setIntegrationForm(prev => ({ ...prev, qbo_client_secret: e.target.value }))}
                            placeholder={integrations?.qbo_client_secret_configured ? '••••••••••••••• (configured)' : 'Enter QuickBooks Client Secret'}
                            className={`pr-10 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                            data-testid="qbo-client-secret-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowQboSecret(!showQboSecret)}
                          >
                            {showQboSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                        {integrations?.qbo_client_secret_configured && (
                          <Badge variant="outline" className="w-fit text-green-500 border-green-500">
                            <CheckCircle className="h-3 w-3 mr-1" /> Secret Configured
                          </Badge>
                        )}
                      </div>
                      
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Redirect URI</Label>
                        <Input
                          value={integrationForm.qbo_redirect_uri}
                          onChange={(e) => setIntegrationForm(prev => ({ ...prev, qbo_redirect_uri: e.target.value }))}
                          placeholder="https://yourdomain.com/api/quickbooks/callback"
                          className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                          data-testid="qbo-redirect-uri-input"
                        />
                        {integrations?.qbo_redirect_uri && (
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            Current: {integrations.qbo_redirect_uri}
                          </p>
                        )}
                      </div>
                      
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Environment</Label>
                        <Select
                          value={integrationForm.qbo_environment}
                          onValueChange={(value) => setIntegrationForm(prev => ({ ...prev, qbo_environment: value }))}
                        >
                          <SelectTrigger className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'} data-testid="qbo-environment-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sandbox">Sandbox (Development)</SelectItem>
                            <SelectItem value="production">Production</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  {/* Google Places Section */}
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                    <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Google Places API
                    </h3>
                    
                    <div className="grid gap-2">
                      <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>API Key</Label>
                      <Input
                        type="password"
                        value={integrationForm.google_places_api_key}
                        onChange={(e) => setIntegrationForm(prev => ({ ...prev, google_places_api_key: e.target.value }))}
                        placeholder={integrations?.google_places_configured ? '••••••••••••••• (configured)' : 'Enter Google Places API Key'}
                        className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                        data-testid="google-places-api-key-input"
                      />
                      {integrations?.google_places_configured && (
                        <Badge variant="outline" className="w-fit text-green-500 border-green-500">
                          <CheckCircle className="h-3 w-3 mr-1" /> API Key Configured
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Resend Email Section */}
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Resend Email Service
                      </h3>
                      <div className="flex items-center gap-2">
                        <Label className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Enable</Label>
                        <Switch
                          checked={resendForm.enabled}
                          onCheckedChange={(checked) => setResendForm(prev => ({ ...prev, enabled: checked }))}
                          disabled={!isAdmin}
                        />
                      </div>
                    </div>
                    
                    {/* Status indicator */}
                    {integrations?.resend?.configured && (
                      <div className={`flex items-center gap-2 mb-4 p-2 rounded ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                          API Key: {integrations.resend.api_key_last4}
                          {integrations.resend.sender_email && (
                            <span className="text-slate-400"> • Sender: {integrations.resend.sender_email}</span>
                          )}
                        </span>
                        {integrations.resend.enabled && (
                          <Badge className="bg-green-500/20 text-green-400 ml-2">Active</Badge>
                        )}
                      </div>
                    )}
                    
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>API Key</Label>
                        <div className="relative">
                          <Input
                            type={showResendApiKey ? 'text' : 'password'}
                            value={resendForm.api_key}
                            onChange={(e) => setResendForm(prev => ({ ...prev, api_key: e.target.value }))}
                            placeholder={integrations?.resend?.configured ? '••••••••••••••• (configured)' : 're_xxxxxxxxxx...'}
                            className={`pr-10 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                            data-testid="resend-api-key-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowResendApiKey(!showResendApiKey)}
                          >
                            {showResendApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          Get your API key from{' '}
                          <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#ffd108] hover:underline">
                            Resend Dashboard
                          </a>
                        </p>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Sender Email</Label>
                          <Input
                            value={resendForm.sender_email}
                            onChange={(e) => setResendForm(prev => ({ ...prev, sender_email: e.target.value }))}
                            placeholder="noreply@equiprcore.com"
                            className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                            data-testid="resend-sender-email-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Sender Name</Label>
                          <Input
                            value={resendForm.sender_name}
                            onChange={(e) => setResendForm(prev => ({ ...prev, sender_name: e.target.value }))}
                            placeholder="EQUIPR CORE"
                            className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                            data-testid="resend-sender-name-input"
                          />
                        </div>
                      </div>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                        Domain must be verified in Resend
                      </p>
                      
                      {/* Webhook Secret for Email Status Tracking */}
                      <div className="grid gap-2 pt-4 border-t border-slate-700/50">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                          Webhook Secret (for Email Status Tracking)
                        </Label>
                        <div className="relative">
                          <Input
                            type={showResendWebhookSecret ? 'text' : 'password'}
                            value={resendForm.webhook_secret}
                            onChange={(e) => setResendForm(prev => ({ ...prev, webhook_secret: e.target.value }))}
                            placeholder={resendForm.webhook_configured ? '••••••••••••••• (configured)' : 'whsec_xxxxxxxxxx...'}
                            className={`pr-10 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                            data-testid="resend-webhook-secret-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowResendWebhookSecret(!showResendWebhookSecret)}
                          >
                            {showResendWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                        {resendForm.webhook_configured && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            <span className={`text-xs ${isDark ? 'text-green-400' : 'text-green-600'}`}>Webhook configured</span>
                          </div>
                        )}
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          Configure webhook in{' '}
                          <a href="https://resend.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-[#ffd108] hover:underline">
                            Resend Dashboard → Webhooks
                          </a>
                          {' '}to track email delivery status (delivered, opened, bounced).
                          <br />
                          Webhook URL: <code className="bg-slate-800 px-1 rounded text-[#ffd108]">{window.location.origin}/api/webhooks/resend</code>
                        </p>
                      </div>
                      
                      {/* Test Results */}
                      {resendTestResult && (
                        <div className={`p-3 rounded ${resendTestResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                          {resendTestResult.success ? (
                            <div className="space-y-1">
                              <p className="text-green-400 font-medium flex items-center gap-2">
                                <CheckCircle className="h-4 w-4" /> Test Email Sent
                              </p>
                              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                                {resendTestResult.message}
                              </p>
                            </div>
                          ) : (
                            <p className="text-red-400 flex items-center gap-2">
                              <XCircle className="h-4 w-4" /> {resendTestResult.message}
                            </p>
                          )}
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={testResendConnection}
                          disabled={testingResend || !integrations?.resend?.configured}
                          className={isDark ? 'border-slate-600' : ''}
                        >
                          {testingResend ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-2" />
                          )}
                          Send Test Email
                        </Button>
                        <Button
                          onClick={saveResendConfig}
                          disabled={integrationsSaving || !isAdmin}
                          className="bg-[#ffd108] text-black hover:bg-[#e5bc00]"
                        >
                          {integrationsSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                          Save Resend Config
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Sales Email Configuration */}
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2 rounded ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                        <Mail className="h-5 w-5 text-[#ffd108]" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Sales Configuration</h4>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Email for Professional plan quote requests</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Sales Email Address</Label>
                        <div className="flex gap-2">
                          <Input
                            value={salesEmail}
                            onChange={(e) => setSalesEmail(e.target.value)}
                            placeholder="sales@equiprcore.com"
                            className={`flex-1 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                            data-testid="sales-email-input"
                          />
                          <Button
                            onClick={saveSalesSettings}
                            disabled={savingSalesEmail || !isAdmin}
                            className="bg-[#ffd108] text-black hover:bg-[#e5bc00]"
                          >
                            {savingSalesEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                            Save
                          </Button>
                        </div>
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          Professional plan quote requests will be sent to this email address
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Support Inbox Configuration */}
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2 rounded ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                        <Mail className="h-5 w-5 text-[#ffd108]" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Support Inbox</h4>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Central inbox for new / unassigned ticket notifications</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Support Inbox Email Address</Label>
                        <div className="flex gap-2">
                          <Input
                            value={supportInboxEmail}
                            onChange={(e) => setSupportInboxEmail(e.target.value)}
                            placeholder="support@equiprcore.com"
                            className={`flex-1 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                            data-testid="support-inbox-email-input"
                          />
                          <Button
                            onClick={saveSupportInbox}
                            disabled={savingSupportInbox || !isAdmin}
                            className="bg-[#ffd108] text-black hover:bg-[#e5bc00]"
                            data-testid="support-inbox-save-button"
                          >
                            {savingSupportInbox ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                            Save
                          </Button>
                        </div>
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          New and unassigned support ticket notifications go here. Assigned tickets notify the assignee directly. Requires the <span className="font-mono">support_ticket_emails</span> feature flag.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* JIRA Integration Configuration */}
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2 rounded ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                        <Link2 className="h-5 w-5 text-[#ffd108]" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>JIRA (Support Ticket Sync)</h4>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Link support tickets to JIRA work items and show their live status</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>JIRA Site</Label>
                          <Input
                            value={jiraSite}
                            onChange={(e) => setJiraSite(e.target.value)}
                            placeholder="yourcompany (or yourcompany.atlassian.net)"
                            className={`${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                            data-testid="jira-site-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Service Account Email</Label>
                          <Input
                            value={jiraEmail}
                            onChange={(e) => setJiraEmail(e.target.value)}
                            placeholder="service@yourcompany.com"
                            className={`${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                            data-testid="jira-email-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>API Token</Label>
                        <div className="flex gap-2">
                          <Input
                            type="password"
                            value={jiraToken}
                            onChange={(e) => setJiraToken(e.target.value)}
                            placeholder={jiraTokenMasked ? `Saved (${jiraTokenMasked}) — leave blank to keep` : 'Paste API token from id.atlassian.com'}
                            className={`flex-1 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                            data-testid="jira-token-input"
                          />
                          <Button
                            onClick={saveJiraSettings}
                            disabled={savingJira || !isAdmin}
                            className="bg-[#ffd108] text-black hover:bg-[#e5bc00]"
                            data-testid="jira-save-button"
                          >
                            {savingJira ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                            Save
                          </Button>
                        </div>
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          Create a token at <span className="font-mono">id.atlassian.com → Security → API tokens</span>. The service account needs "Browse projects" on the relevant JIRA project. Token is stored encrypted.
                        </p>
                      </div>

                      {/* Two-way sync config: default project + issue type for "Create issue" */}
                      <div className={`pt-4 mt-2 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                        <p className={`text-sm font-medium mb-3 ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>
                          Create-issue defaults (for pushing tickets into JIRA)
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Project Key</Label>
                            <div className="flex gap-2">
                              <Input
                                value={jiraProjectKey}
                                onChange={(e) => setJiraProjectKey(e.target.value.toUpperCase())}
                                placeholder="e.g. SUP"
                                className={`flex-1 font-mono ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}`}
                                data-testid="jira-project-key-input"
                              />
                              <Button
                                variant="outline"
                                onClick={loadJiraIssueTypes}
                                disabled={loadingIssueTypes || !jiraProjectKey.trim()}
                                data-testid="jira-load-issue-types-button"
                              >
                                {loadingIssueTypes ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load types'}
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Issue Type</Label>
                            <Select
                              value={jiraIssueTypeId}
                              onValueChange={(val) => {
                                setJiraIssueTypeId(val);
                                const t = jiraIssueTypes.find((it) => it.id === val);
                                setJiraIssueTypeName(t ? t.name : '');
                              }}
                            >
                              <SelectTrigger
                                className={isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white'}
                                data-testid="jira-issue-type-select"
                              >
                                <SelectValue placeholder={jiraIssueTypeName || 'Load types, then select'} />
                              </SelectTrigger>
                              <SelectContent>
                                {jiraIssueTypes.filter((t) => !t.subtask).map((t) => (
                                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          Needed for the "Create JIRA issue" button on tickets. The service account also needs "Create issues" and "Add comments" on this project.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={saveIntegrations}
                      disabled={integrationsSaving || !isAdmin}
                      className="bg-[#ffd108] text-black hover:bg-[#e5bc00]"
                      data-testid="save-integrations-button"
                    >
                      {integrationsSaving ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save Integration Credentials
                    </Button>
                  </div>
                  
                  <Alert className={isDark ? 'bg-slate-900 border-slate-700' : 'bg-blue-50 border-blue-200'}>
                    <AlertTriangle className={`h-4 w-4 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                    <AlertDescription className={isDark ? 'text-slate-300' : 'text-gray-600'}>
                      <strong>Important:</strong> These credentials are stored securely and apply to all tenants using these integrations.
                      Changing credentials may require tenants to reconnect their integrations.
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Database Settings */}
        <TabsContent value="database" className="space-y-4">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Database className="h-5 w-5 text-[#ffd108]" />
                MongoDB Connection
              </CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                Database connection status and information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Connection Status */}
              <div className={`p-4 rounded-lg space-y-4 ${isDark ? 'bg-slate-900' : 'bg-gray-100'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {mongoStatus?.connected ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Connection Status</p>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {mongoStatus?.connected ? 'Connected' : 'Disconnected'}
                      </p>
                    </div>
                  </div>
                  {mongoStatus?.is_atlas && (
                    <Badge className="bg-green-500/20 text-green-400">MongoDB Atlas</Badge>
                  )}
                </div>

                {mongoStatus?.connected && (
                  <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Database</p>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{mongoStatus.database}</p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Version</p>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{mongoStatus.version}</p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Collections</p>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{mongoStatus.collections}</p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Data Size</p>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{mongoStatus.data_size_mb} MB</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Friendly Name */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Connection Name (Display)</Label>
                <div className="flex gap-2">
                  <Input
                    value={mongoName}
                    onChange={(e) => setMongoName(e.target.value)}
                    placeholder="e.g., Production Atlas, Staging DB"
                    className={`${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                    disabled={!isAdmin}
                  />
                  {isAdmin && (
                    <Button
                      onClick={saveMongoName}
                      disabled={saving}
                      variant="outline"
                      className={isDark ? 'border-slate-600' : 'border-gray-300'}
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>A friendly name to identify this database connection</p>
              </div>

              {/* Atlas Info */}
              <Alert className={isDark ? 'bg-blue-500/10 border-blue-500' : 'bg-blue-50 border-blue-200'}>
                <Server className="h-4 w-4 text-blue-400" />
                <AlertDescription className={isDark ? 'text-blue-300' : 'text-blue-700'}>
                  <p className="font-medium mb-1">MongoDB Atlas Configuration</p>
                  <p className="text-sm">
                    The MongoDB connection string is configured in the server environment variables. 
                    To change databases, update the <code className={`${isDark ? 'bg-slate-800' : 'bg-blue-100'} px-1 rounded`}>MONGO_URL</code> environment variable and restart the server.
                  </p>
                </AlertDescription>
              </Alert>

              {/* Refresh Button */}
              <Button
                variant="outline"
                onClick={fetchMongoStatus}
                className={isDark ? 'border-slate-600' : 'border-gray-300'}
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh Status
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Legal Documents Tab */}
        <TabsContent value="legal" className="space-y-4">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <FileText className="h-5 w-5" /> Legal Documents
              </CardTitle>
              <CardDescription>
                Upload EULA and Privacy Policy PDFs. These are publicly accessible at <code>/eula</code> and <code>/privacy</code>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <LegalDocUpload docType="privacy" label="Privacy Policy" isDark={isDark} />
              <LegalDocUpload docType="eula" label="EULA (End User License Agreement)" isDark={isDark} />
              <LegalDocUpload docType="ea_terms" label="Early Adopter Terms & Conditions" isDark={isDark} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tickets Settings */}
        <TabsContent value="tickets" className="space-y-4">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Ticket className="h-5 w-5 text-[#ffd108]" /> Ticket Auto-Close
              </CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                Automatically close resolved tickets after a period of inactivity. Set to 0 to disable.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end gap-4">
                <div className="space-y-1.5">
                  <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Days after resolution</Label>
                  <Input
                    type="number"
                    min={0}
                    max={90}
                    value={autoCloseDays}
                    onChange={(e) => setAutoCloseDays(parseInt(e.target.value) || 0)}
                    className={`w-28 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                    data-testid="ticket-auto-close-days"
                  />
                </div>
                <Button
                  onClick={saveAutoCloseDays}
                  disabled={savingAutoClose}
                  className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                  data-testid="save-auto-close-btn"
                >
                  {savingAutoClose ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save
                </Button>
              </div>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                {autoCloseDays === 0
                  ? 'Auto-close is disabled. Resolved tickets must be closed manually.'
                  : `Resolved tickets will be auto-closed after ${autoCloseDays} day${autoCloseDays !== 1 ? 's' : ''} with no activity. An internal note is added when this happens.`}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Last Updated */}
      {settings?.updated_at && (
        <p className={`text-xs text-right ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
          Last updated: {formatDateTime(settings.updated_at)} by {settings.updated_by || 'System'}
        </p>
      )}
    </div>
  );
};

export default SystemSettings;

function LegalDocUpload({ docType, label, isDark }) {
  const { api } = useSuperAdmin();
  const [docInfo, setDocInfo] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await api.get(`/api/legal/${docType}`);
        setDocInfo(res.data);
      } catch (err) {
        console.error(`Failed to fetch ${docType} info:`, err);
      }
    };
    fetchInfo();
  }, [docType, api]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/api/legal/upload?doc_type=${docType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      setDocInfo({ exists: true, filename: file.name, updated_at: new Date().toISOString(), doc_type: docType });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const appUrl = window.location.origin.replace(/\/super-admin.*/, '');

  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</h3>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            Public URL: <a href={`/${docType}`} target="_blank" rel="noopener noreferrer" className="text-[#ffd108] hover:underline">{appUrl}/{docType}</a>
          </p>
        </div>
        {docInfo?.exists ? (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Uploaded</Badge>
        ) : (
          <Badge variant="outline" className={isDark ? 'border-slate-600 text-slate-400' : ''}>Not uploaded</Badge>
        )}
      </div>

      {docInfo?.exists && (
        <div className={`text-xs mb-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          File: {docInfo.filename} | Updated: {docInfo.updated_at ? formatDate(docInfo.updated_at) : 'Unknown'}
        </div>
      )}

      <label className="cursor-pointer inline-flex items-center gap-2" data-testid={`upload-${docType}-btn`}>
        <input type="file" accept="application/pdf" onChange={handleUpload} className="hidden" />
        <Button variant="outline" size="sm" asChild disabled={uploading}>
          <span>
            {uploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
            {docInfo?.exists ? 'Replace PDF' : 'Upload PDF'}
          </span>
        </Button>
      </label>
    </div>
  );
}
